import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Refresh as RefreshIcon,
  AccountCircle,
  Login,
  Logout,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import { Transaction, Budget, SavingsGoal } from '../types';
import { RealTimeSyncService } from '../services/realTimeSyncService';

interface SettingsProps {
  transactions: Transaction[];
  budgets: Budget[];
  savingsGoals: SavingsGoal[];
  onDataSync: (data: {
    transactions: Transaction[];
    budgets: Budget[];
    savingsGoals: SavingsGoal[];
  }) => void;
  syncService?: RealTimeSyncService | null;
}

const Settings: React.FC<SettingsProps> = ({ transactions, budgets, savingsGoals, onDataSync, syncService }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentUser, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const clearLocalStorage = () => {
    if (window.confirm('Êtes-vous sûr de vouloir effacer toutes les données ? Cette action est irréversible.')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        ⚙️ Paramètres
      </Typography>

      {/* Apparence */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PaletteIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Apparence</Typography>
          </Box>
          
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={toggleTheme}
                icon={<LightModeIcon />}
                checkedIcon={<DarkModeIcon />}
                size="medium"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  Mode sombre
                </Typography>
                {isDarkMode ? 
                  <DarkModeIcon sx={{ ml: 1, fontSize: 20, color: 'primary.main' }} /> : 
                  <LightModeIcon sx={{ ml: 1, fontSize: 20, color: 'warning.main' }} />
                }
              </Box>
            }
            sx={{ width: 'fit-content' }}
          />
        </CardContent>
      </Card>

      {/* Authentification et Synchronisation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountCircle sx={{ mr: 1 }} />
            <Typography variant="h6">Compte et Synchronisation</Typography>
          </Box>
          
          {currentUser ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connecté en tant que: <strong>{currentUser.email}</strong>
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                {syncService ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Synchronisation:</strong> Active en temps réel
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Statut:</strong> {syncService.getSyncStatus().isOnline ? '🟢 En ligne' : '🔴 Hors ligne'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Listeners:</strong> {syncService.getSyncStatus().hasListeners ? '✅ Actifs' : '❌ Inactifs'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Les données sont automatiquement synchronisées entre vos appareils.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Initialisation de la synchronisation...
                  </Typography>
                )}
              </Box>
              
              <Button
                variant="outlined"
                startIcon={<Logout />}
                onClick={logout}
                color="error"
              >
                Se déconnecter
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connectez-vous pour synchroniser vos données entre appareils et sauvegarder dans le cloud.
              </Typography>
              
              <Button
                variant="contained"
                startIcon={<Login />}
                onClick={() => setShowAuthModal(true)}
              >
                Se connecter
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Données */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🗄️ Gestion des données
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Toutes les données sont stockées localement dans votre navigateur.
          </Typography>
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<RefreshIcon />}
            onClick={clearLocalStorage}
            sx={{ mr: 2 }}
          >
            Réinitialiser toutes les données
          </Button>
        </CardContent>
      </Card>

      {/* Informations */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ℹ️ Informations
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Version:</strong> 2.0.0
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Développé avec:</strong> React + TypeScript + Material-UI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Stockage:</strong> Local Storage du navigateur
          </Typography>
        </CardContent>
      </Card>
      
      {/* Modal d'authentification */}
      <AuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </Box>
  );
};

export default Settings;
